import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import {
  BookOpen, Compass, GraduationCap, Map, Scale, Users, TrendingUp, Settings,
  Sunrise, Sun, Moon, Flame, Quote, LogOut, ChevronRight, Library,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/LanguageContext";
import JoinTheJourney from "@/pages/mystoicos/JoinTheJourney";

const API = process.env.REACT_APP_BACKEND_URL;
const serif = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const localToday = () => new Date().toLocaleDateString("sv-SE");

const MODULES = [
  { key: "journal", icon: BookOpen, to: "/my-stoicos/journal", live: true },
  { key: "academy", icon: GraduationCap, to: "/academy", live: true },
  { key: "library", icon: Library, to: "/library", live: true },
  { key: "journeys", icon: Map },
  { key: "virtues", icon: Scale },
  { key: "mentors", icon: Users },
  { key: "progress", icon: TrendingUp },
  { key: "settings", icon: Settings, to: "/my-stoicos/settings", live: true },
];

const card = "border border-white/10 bg-[#0b0b0b] p-6 md:p-7";

function PracticeRow({ icon: Icon, label, done, t }) {
  return (
    <Link
      to="/my-stoicos/journal"
      className="flex items-center justify-between py-3 border-b border-white/5 last:border-0 group"
    >
      <span className="flex items-center gap-3 text-sm text-bone/80">
        <Icon size={16} strokeWidth={1.5} className="text-gold/80" />
        {label}
      </span>
      <span className={`text-[10px] tracking-[0.2em] uppercase ${done ? "text-gold" : "text-bone/35 group-hover:text-bone/60"}`}>
        {done ? t("my.done") : t("my.open")}
      </span>
    </Link>
  );
}

export default function MyStoicOs() {
  const { user, membership, loading, logout } = useAuth();
  const { t, lang } = useT();
  const [dash, setDash] = useState(null);
  // Chosen Academy discipline before registration/login (read once; removal in effect keeps render pure).
  const [pendingRedirect] = useState(() => sessionStorage.getItem("postAuthRedirect"));

  useEffect(() => {
    if (user && pendingRedirect) sessionStorage.removeItem("postAuthRedirect");
  }, [user, pendingRedirect]);

  useEffect(() => {
    if (!user) return;
    const load = () =>
      axios
        .get(`${API}/api/me/dashboard`, { params: { today: localToday() }, withCredentials: true })
        .then(({ data }) => setDash(data))
        .catch(() => {});
    load();
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, [user]);

  if (loading) {
    return <main className="min-h-screen bg-[#050505] pt-40 text-center text-bone/40 text-sm">…</main>;
  }
  if (!user) return <JoinTheJourney />;

  if (pendingRedirect) return <Navigate to={pendingRedirect} replace />;

  if (!user.onboarding_completed) {
    const resume = Math.min(Math.max(user.onboarding_current_scene || 1, 1), 6);
    return <Navigate to={`/my-stoicos/onboarding/${resume}`} replace />;
  }

  const sinceDate = membership?.member_since
    ? new Date(membership.member_since).toLocaleDateString(lang === "nl" ? "nl-NL" : "en-GB", { day: "numeric", month: "long", year: "numeric" })
    : "";
  const tp = dash?.today_practice || {};

  return (
    <main data-testid="mystoicos-dashboard" className="min-h-screen bg-[#050505] pt-32 pb-24 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <p className="text-[11px] tracking-[0.28em] uppercase text-gold mb-4">{t("my.eyebrow")}</p>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-4">
          <h1 style={serif} data-testid="mystoicos-welcome" className="text-4xl sm:text-5xl lg:text-6xl text-bone font-medium tracking-tight">
            {t("my.welcome")}, {t("my.traveler")}
          </h1>
          <button
            data-testid="mystoicos-logout-button"
            onClick={logout}
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-bone/40 hover:text-gold transition-colors pb-2"
          >
            <LogOut size={13} strokeWidth={1.5} /> {t("my.logout")}
          </button>
        </div>
        <p className="text-base text-bone/50 mb-8">{t("my.subtitle")}</p>

        {/* Membership strip */}
        <div data-testid="membership-strip" className="flex flex-wrap items-center gap-x-8 gap-y-2 border border-gold/25 bg-gold/[0.04] px-6 py-4 mb-5">
          <span className="text-[11px] tracking-[0.22em] uppercase text-gold">
            {membership?.status === "member" ? "Member" : t("my.aspirant")}
          </span>
          <span className="text-[11px] tracking-[0.16em] uppercase text-bone/50">
            {t("my.memberSince")} · {sinceDate}
          </span>
          <span data-testid="membership-number" className="text-[11px] tracking-[0.16em] uppercase text-bone/50">
            № {membership?.membership_number}
          </span>
          {membership?.intro_phase === "introduction" && (
            <span className="text-[11px] tracking-[0.16em] uppercase text-bone/50">
              {t("my.introDay", { day: membership.intro_day })}
            </span>
          )}
        </div>

        {/* Recommended first step — Logic */}
        <div data-testid="recommended-logic" className="border border-gold/40 bg-gold/[0.05] p-6 md:p-8 mb-14 flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex-1">
            <p className="text-[11px] tracking-[0.28em] uppercase text-gold mb-3">{t("my.recFirst")}</p>
            <h2 style={serif} className="text-2xl md:text-3xl text-bone font-medium tracking-tight mb-2">{t("my.startLogic")}</h2>
            <p className="text-sm text-bone/60 max-w-md">{t("my.logicSub")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              data-testid="begin-logic-button"
              to="/academy/logic"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-ink bg-gold hover:bg-gold-hover px-7 py-3.5 transition-colors"
            >
              {t("my.beginLogic")} <ChevronRight size={14} strokeWidth={1.5} />
            </Link>
            <Link
              data-testid="ethics-link"
              to="/academy/ethics"
              className="inline-flex items-center text-[11px] tracking-[0.24em] uppercase border border-bone/30 text-bone/80 hover:border-gold hover:text-gold px-5 py-3.5 transition-colors"
            >
              Ethics
            </Link>
            <Link
              data-testid="physics-link"
              to="/academy/physics"
              className="inline-flex items-center text-[11px] tracking-[0.24em] uppercase border border-bone/30 text-bone/80 hover:border-gold hover:text-gold px-5 py-3.5 transition-colors"
            >
              Physics
            </Link>
          </div>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
          <div data-testid="dash-today-practice" className={`${card} lg:col-span-2`}>
            <h2 className="text-[11px] tracking-[0.24em] uppercase text-gold mb-4">{t("my.todaysPractice")}</h2>
            <PracticeRow icon={Sunrise} label={t("my.morning")} done={tp.morning} t={t} />
            <PracticeRow icon={Sun} label={t("my.practice")} done={tp.practice} t={t} />
            <PracticeRow icon={Moon} label={t("my.evening")} done={tp.evening} t={t} />
            <Link
              data-testid="journal-cta"
              to="/my-stoicos/journal"
              className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-ink bg-gold hover:bg-gold-hover px-6 py-3 transition-colors"
            >
              {t("my.journalCta")} <ChevronRight size={14} strokeWidth={1.5} />
            </Link>
          </div>

          <div data-testid="dash-virtue-focus" className={card}>
            <h2 className="text-[11px] tracking-[0.24em] uppercase text-gold mb-4">{t("my.virtueFocus")}</h2>
            <p style={serif} className="text-3xl text-bone font-medium">
              {dash ? t(`my.virtue.${dash.virtue_focus}`) : "—"}
            </p>
          </div>

          <div data-testid="dash-streak" className={card}>
            <h2 className="text-[11px] tracking-[0.24em] uppercase text-gold mb-4">{t("my.streak")}</h2>
            <p style={serif} className="text-3xl text-bone font-medium flex items-center gap-2.5">
              <Flame size={22} strokeWidth={1.5} className="text-gold" />
              {(dash?.streak ?? 0) === 1 ? t("my.streakDay") : t("my.streakDays", { n: dash?.streak ?? 0 })}
            </p>
            <p className="text-xs text-bone/40 mt-2">{dash?.days_practiced ?? 0} {t("my.daysPracticed")}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-14">
          <div data-testid="dash-inspiration" className={`${card} lg:col-span-2`}>
            <h2 className="text-[11px] tracking-[0.24em] uppercase text-gold mb-4 flex items-center gap-2">
              <Quote size={13} strokeWidth={1.5} /> {t("my.inspiration")}
            </h2>
            <p style={serif} className="text-xl md:text-2xl text-bone/90 italic leading-relaxed">
              "{dash?.daily_inspiration?.text}"
            </p>
            <p className="text-xs tracking-[0.18em] uppercase text-bone/40 mt-3">— {dash?.daily_inspiration?.author}</p>
          </div>

          <div data-testid="dash-recommendations" className={card}>
            <h2 className="text-[11px] tracking-[0.24em] uppercase text-gold mb-4 flex items-center gap-2">
              <Compass size={13} strokeWidth={1.5} /> {t("my.recommendations")}
            </h2>
            {(dash?.recommendations || []).map((r) => (
              <Link key={r.key} to={r.to} className="flex items-center justify-between py-2.5 text-sm text-bone/70 hover:text-gold transition-colors group">
                {t(`my.rec.${r.key}`)}
                <ChevronRight size={14} strokeWidth={1.5} className="text-bone/30 group-hover:text-gold" />
              </Link>
            ))}
          </div>
        </div>

        {/* Recent activity */}
        <div data-testid="dash-recent-activity" className={`${card} mb-14`}>
          <h2 className="text-[11px] tracking-[0.24em] uppercase text-gold mb-4">{t("my.recent")}</h2>
          {dash?.recent_activity?.length ? (
            <div className="divide-y divide-white/5">
              {dash.recent_activity.map((e) => (
                <Link key={e.id} to="/my-stoicos/journal" className="flex items-baseline gap-4 py-3 group">
                  <span className="text-[10px] tracking-[0.18em] uppercase text-gold/70 w-32 shrink-0">
                    {t(`my.${e.entry_type === "free" ? "freeLabel" : e.entry_type}`)}
                  </span>
                  <span className="text-sm text-bone/60 truncate group-hover:text-bone/90 transition-colors">
                    {e.title || (e.content || "").slice(0, 90)}
                  </span>
                  <span className="ml-auto text-xs text-bone/30 shrink-0">{e.date}</span>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-sm text-bone/40">{t("my.noActivity")}</p>
          )}
        </div>

        {/* Modules — the study */}
        <h2 style={serif} className="text-2xl md:text-3xl text-bone font-medium tracking-tight mb-6">{t("my.modules")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {MODULES.map(({ key, icon: Icon, to, live }) => {
            const inner = (
              <div
                data-testid={`module-tile-${key}`}
                className={`h-full border p-6 transition-colors ${
                  live ? "border-gold/40 bg-gold/[0.05] hover:border-gold" : "border-white/8 bg-[#0b0b0b]"
                }`}
              >
                <Icon size={20} strokeWidth={1.5} className={live ? "text-gold" : "text-bone/30"} />
                <p className={`mt-4 text-sm tracking-[0.12em] uppercase ${live ? "text-bone" : "text-bone/50"}`}>
                  {t(`my.module.${key}`)}
                </p>
                <p className="mt-1.5 text-xs leading-relaxed text-bone/40">{t(`my.module.${key}.desc`)}</p>
                {!live && (
                  <p className="mt-3 text-[10px] tracking-[0.2em] uppercase text-gold/50">{t("my.inPreparation")}</p>
                )}
              </div>
            );
            return live ? (
              <Link key={key} to={to}>{inner}</Link>
            ) : (
              <div key={key}>{inner}</div>
            );
          })}
        </div>
      </div>
    </main>
  );
}
